import { Injectable } from '@angular/core';
import { BaseJsonService } from '@shared/services/base.service';
import { Observable, of } from 'rxjs';
import { catchError, map, shareReplay, switchMap } from 'rxjs/operators';
import { ListResponse, ObjectResponse } from '@shared/models';
import { OrderSearchParams } from 'src/models/orders/orderSearchParams.model';
import { SelectItem } from 'src/models/general/select.item.model';
import { OrderListResponse } from 'src/models/orders/orderListResponse';
import { OrderDetailModel } from 'src/models/orders/orderDetails/orderDetail.model';
import { ConfirmOrderArticle } from 'src/models/orders/orderConfirm/confirmOrderArticle.model';
import {
	CancelOrderUpdate,
	ExportConfirmData,
	OrderUpdate,
	PaymentMethodUpdate,
	RemarksUpdate,
	ReviewStateUpdate
} from 'src/models/orders/order.service.models';
import { VatValidationResult } from 'src/models/orders/vat/vat.validation.result.model';
import { OrderItemModel } from 'src/models/orders/orderItems/orderItems.model';
import { SalesChannelWithWorkflowStatus } from 'src/models/orders/salesChannelWithWorkflowStatus';
import { OrderInvoice, OrderInvoicePosition } from 'src/models/orders/invoice/order.invoice.model';
import { Shipment, ShipmentPosition, TrackingNumber } from 'src/models/orders/orderDetails/shipment.model';
import { SalesChannelWithAvailableActions } from 'src/models/orders/salesChannelWithAvailableActions';

@Injectable({
	providedIn: 'root'
})
export class OrderService extends BaseJsonService {
	private workflowStates$: Observable<SalesChannelWithWorkflowStatus> | null = null;
	private salesChannelActions$: Observable<SalesChannelWithAvailableActions> | null = null;

	getOrders(params: OrderSearchParams): Observable<OrderListResponse> {
		const url = this.generateUrl(this.services.order, '%s', 'order', params.page);

		// Clone to not cache filtered searches
		const options = {...this.options};
		options.params = params.getHttpQueryParams();
		return this.http.get<ObjectResponse<OrderListResponse>>(url, options).pipe(
			map((res: ObjectResponse<OrderListResponse>) => {
				return res.data;
			})
		);
	}

	getOrderById(orderId: string): Observable<OrderDetailModel> {
		const url = this.generateUrl(this.services.order, 'order/%s', orderId);

		return this.http.get<ObjectResponse<OrderDetailModel>>(url, this.options).pipe(
			map((res: ObjectResponse<OrderDetailModel>) => {
				return res.data;
			})
		);
	}

	confirmOrder(confirmations: ConfirmOrderArticle[], id: string): Observable<void> {
		confirmations.forEach((entry: ConfirmOrderArticle) => {
			entry.quantity = +entry.quantity;
		});

		const confirmUrl = this.generateUrl(this.services.order, 'order/egis/confirm/%s', id);

		return this.http.put<void>(confirmUrl, confirmations, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	confirmOrderExport(exportConfirmData: ExportConfirmData): Observable<void> {
		const url = this.generateUrl(this.services.order, '%s', 'order/exportConfirm');

		return this.http.put<void>(url, exportConfirmData, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	getRejectionReasonsAsSelectItem(): Observable<SelectItem[]> {
		return of(rejectionReasons).pipe(
			map((reasons: string[]) => {
				return reasons.map((reason: string) => {
					return {id: reason, value: reason};
				});
			})
		);
	}

	updateCustomerNumber(value: OrderUpdate, orderId: string): Observable<void> {
		const url = this.generateUrl(this.services.order, '%s/%s', 'order/customerNumber', orderId);

		return this.http.patch<void>(url, value, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	updateReviewState(orderUuid: string, reviewStateUpdate: ReviewStateUpdate): Observable<void> {
		const url = this.generateUrl(this.services.order, '%s/%s', 'order/review', orderUuid);

		return this.http.patch<void>(url, reviewStateUpdate, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	cancelOrder(orderUuid: string, cancellationReason: CancelOrderUpdate): Observable<void> {
		const url = this.generateUrl(this.services.order, '%s/%s', 'order/cancel', orderUuid);
		return this.http.patch<void>(url, cancellationReason, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	updateComment(orderUuid: string, remarks: RemarksUpdate): Observable<void> {
		const url = this.generateUrl(this.services.order, '%s/%s', 'order/remarks', orderUuid);

		return this.http.patch<void>(url, remarks, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	updateOrder(order: OrderDetailModel): any {
		const url = this.generateUrl(this.services.order, 'order');

		return this.http.put<ObjectResponse<OrderDetailModel>>(url, order, this.options).pipe(
			map((response: ObjectResponse<OrderDetailModel>) => {
				return response.data;
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	setOrderPaid(orderId: string): any {
		const url = this.generateUrl(this.services.order, 'order/paid/%s', orderId);

		return this.http.patch<void>(url, {}, this.options).pipe(
			map(() => {
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	validateVatId(order: OrderDetailModel, qualified: boolean = true): Observable<VatValidationResult> {
		const url = this.generateUrl(this.services.order, 'order/vatcheck/%s/%s', order.id, qualified);

		return this.http.get<ObjectResponse<VatValidationResult>>(url, this.options).pipe(
			map((res: ObjectResponse<VatValidationResult>) => {
				return res.data;
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	getVatIdLogs(orderUuid: string, onlyLatestEntry: boolean): Observable<VatValidationResult[]> {
		const url = this.generateUrl(this.services.order, 'order/vatcheckresult/%s/%s', orderUuid, onlyLatestEntry);
		return this.http.get<ListResponse<VatValidationResult>>(url, this.options).pipe(
			map((res: ListResponse<VatValidationResult>) => {
				return res.data;
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	updateOrderItems(orderUuid: string, oldOrderItems: OrderItemModel[]): Observable<void> {
		const url = this.generateUrl(this.services.order, 'order/items/%s', orderUuid);

		return this.http.put<void>(url, {orderItems: oldOrderItems}, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	updatePaymentMethod(orderUuid: string, paymentMethodUpdate: PaymentMethodUpdate): Observable<void> {
		const url = this.generateUrl(this.services.order, '%s/%s', 'order/payment/method', orderUuid);

		return this.http.patch<void>(url, paymentMethodUpdate, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}


	fakeBaselineConfirm(orderId: string): Observable<void> {
		const url = this.generateUrl(this.services.order, 'import/confirmation');

		return this.http.put<void>(url, {
			uuid: orderId,
			success: true,
			orderId: 'baseline-fake-id',
			errorMessage: ''
		}, this.options).pipe(
			catchError(BaseJsonService.handleError)
		);
	}

	fakeCompleteShipment(orderId: string): Observable<void> {
		const url = this.generateUrl(this.services.order, 'import/shipment');

		return this.getOrderById(orderId).pipe(switchMap((orderDetails: OrderDetailModel) => {
				const fakeShipmentModel: Shipment = {
					shipmentPositions: orderDetails.orderItems.map((item: OrderItemModel) => {
						return {shippingQuantity: item.orderedAmount, productNumber: item.sku} as ShipmentPosition;
					}),
					shipmentDate: new Date().toISOString(),
					orderUuid: orderId,
					shipmentNumber: '12345',
					trackingNumbers: [{trackingNumber: '12345-fake-tn', serviceProvider: 'DHL'} as TrackingNumber],
				};

				return this.http.put<void>(url, fakeShipmentModel, this.options);
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	fakeInvoice(orderId: string): Observable<void> {
		const url = this.generateUrl(this.services.order, 'import/invoice');

		return this.getOrderById(orderId).pipe(switchMap((orderDetails: OrderDetailModel) => {
				const fakeInvoiceModel: OrderInvoice = {
					invoicePositions: orderDetails.orderItems.map((item: OrderItemModel) => {
						return {invoicedQuantity: item.orderedAmount, productNumber: item.sku} as OrderInvoicePosition;
					}),
					orderUuid: orderId,
					invoiceNumber: 'fake-invoice-number',
					invoiceDate: new Date().toISOString(),
				};

				return this.http.put<void>(url, fakeInvoiceModel, this.options);
			}),
			catchError(BaseJsonService.handleError)
		);
	}

	getWorkflowStatesFor(): Observable<SalesChannelWithWorkflowStatus> {
		const url = this.generateUrl(this.services.order, 'order/states');

		if (!this.workflowStates$) {
			this.workflowStates$ = this.http.get<ObjectResponse<SalesChannelWithWorkflowStatus>>(url, this.options).pipe(
				map((res: ObjectResponse<SalesChannelWithWorkflowStatus>) => {
					return res.data;
				}),
				shareReplay(),
				catchError(BaseJsonService.handleError)
			);
		}
		return this.workflowStates$;
	}

	getAlwaysAllowedActions(): Observable<SalesChannelWithAvailableActions> {
		const url = this.generateUrl(this.services.order, 'order/alwaysAllowedActions');

		if (!this.salesChannelActions$) {
			this.salesChannelActions$ = this.http.get<ObjectResponse<SalesChannelWithAvailableActions>>(url, this.options).pipe(
				map((res: ObjectResponse<SalesChannelWithAvailableActions>) => {
					return res.data;
				}),
				shareReplay(),
				catchError(BaseJsonService.handleError)
			);
		}
		return this.salesChannelActions$;
	}
}

const rejectionReasons = [
	'Abgelehnt - Bestand zu klein',
	'Abgelehnt - falscher Preis',
	'Abgelehnt - Kundenlimit zu klein',
	'Abgelehnt - Kundenstorno',
	'Abgelehnt - EOL',
];

